import { DatePicker, type DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useId } from 'react';
import { ICalendarProps } from './types';

function toDayjs(value: Dayjs | Date | string | null | undefined): Dayjs | null {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

export const Calendar: FC<ICalendarProps> = ({
  className,
  disabled,
  disableFuture,
  disablePast,
  helperText,
  inputFormat = 'DD/MM/YYYY',
  inputRef,
  label,
  maxDate,
  minDate,
  onChange,
  status,
  value,
}) => {
  const inputId = useId();

  const handleChange = (date: Dayjs | null) => {
    if (!date) {
      onChange(null);
      return;
    }

    const today = dayjs();
    const dateWithTime = date
      .hour(today.hour())
      .minute(today.minute())
      .second(today.second())
      .millisecond(today.millisecond());

    onChange(dateWithTime);
  };

  const disabledDate: DatePickerProps['disabledDate'] = current => {
    if (!current) {
      return false;
    }

    if (disablePast && current.isBefore(dayjs().startOf('day'))) {
      return true;
    }

    if (disableFuture && current.isAfter(dayjs().endOf('day'))) {
      return true;
    }

    if (minDate && current.isBefore(dayjs(minDate).startOf('day'))) {
      return true;
    }

    if (maxDate && current.isAfter(dayjs(maxDate).endOf('day'))) {
      return true;
    }

    return false;
  };

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={inputId} className={'text-ash mb-1 block text-sm'}>
          {label}
        </label>
      ) : null}
      <DatePicker
        id={inputId}
        ref={inputRef}
        size={'large'}
        className={'w-full h-14! text-base [&_input]:text-base'}
        disabled={disabled}
        disabledDate={disabledDate}
        format={inputFormat}
        onChange={handleChange}
        placeholder={label || inputFormat}
        status={status ?? (helperText ? 'error' : undefined)}
        value={toDayjs(value)}
      />
      {helperText ? <div className={'mt-1 text-xs text-red-500'}>{helperText}</div> : null}
    </div>
  );
};
